/**
 * Life4Earth V2 — File Storage API Adapter
 *
 * Uploads/downloads files via the .NET FileStorageController,
 * which proxies to MinIO (S3-compatible object storage).
 */

import { API_BASE_URL, getCurrentUserGuid } from './client';
import { getAccessToken, tryRefreshToken } from '../auth/AuthContext';

/** Build auth headers: prefer JWT Bearer, fall back to X-User-Guid for fake auth */
function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  if (token) return { Authorization: `Bearer ${token}` };
  return { 'X-User-Guid': getCurrentUserGuid() };
}

export interface UploadResult {
  key: string;
  url: string;
  size: number;
  contentType: string;
}

export interface FileEntry {
  key: string;
  url: string;
  size: number;
  lastModified: string;
}

/**
 * Upload a file (image, document, etc.) to MinIO via the API.
 *
 * @param file       The File or Blob to upload
 * @param category   Folder prefix, e.g. "heroes", "logos", "documents"
 * @param entityId   Optional sub-folder, typically a project GUID
 * @param fileName   Override filename (defaults to file.name)
 * @returns          Upload result with the retrieval URL
 */
export async function uploadFile(
  file: File | Blob,
  category: string = 'general',
  entityId?: string,
  fileName?: string
): Promise<UploadResult> {
  const formData = new FormData();
  formData.append('file', file, fileName ?? (file as File).name ?? 'upload.bin');

  const params = new URLSearchParams({ category });
  if (entityId) params.set('entityId', entityId);

  const url = `${API_BASE_URL}/api/FileStorage/upload?${params}`;
  const makeInit = (): RequestInit => ({
    method: 'POST',
    headers: {
      ...authHeaders(),
      // Note: Do NOT set Content-Type — browser sets multipart boundary automatically
    },
    body: formData,
  });

  let response = await fetch(url, makeInit());

  // 401 with expired token — try a transparent refresh and retry once
  // (mirrors the retry logic in apiFetch for consistency)
  if (response.status === 401) {
    const newToken = await tryRefreshToken();
    if (newToken) {
      response = await fetch(url, makeInit());
    }
  }

  if (!response.ok) {
    const errText = await response.text().catch(() => response.statusText);
    throw new Error(`Upload failed (${response.status}): ${errText}`);
  }

  return response.json();
}

/**
 * Build the full URL to retrieve a stored file.
 */
export function fileUrl(key: string): string {
  return `${API_BASE_URL}/api/FileStorage/file/${key}`;
}

/**
 * List files under a given prefix.
 */
export async function listFiles(prefix?: string): Promise<FileEntry[]> {
  const params = prefix ? `?prefix=${encodeURIComponent(prefix)}` : '';
  const response = await fetch(
    `${API_BASE_URL}/api/FileStorage/list${params}`,
    {
      headers: authHeaders(),
    }
  );
  if (!response.ok) throw new Error(`List failed: ${response.statusText}`);
  return response.json();
}

/**
 * Delete a stored file by key.
 */
export async function deleteFile(key: string): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/FileStorage/file/${key}`,
    {
      method: 'DELETE',
      headers: authHeaders(),
    }
  );
  if (!response.ok && response.status !== 404) {
    throw new Error(`Delete failed: ${response.statusText}`);
  }
}

/**
 * Upload a data URL (base64 image) as a file.
 * Converts the data URL to a Blob first.
 */
export async function uploadDataUrl(
  dataUrl: string,
  category: string,
  entityId?: string,
  fileName?: string
): Promise<UploadResult> {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const ext = blob.type.split('/')[1] || 'bin';
  return uploadFile(blob, category, entityId, fileName ?? `image.${ext}`);
}

/* ──────────────────────────────────────────────────────────────────────
 * DEV-822 #126 — small display helpers for attachment lists
 * ────────────────────────────────────────────────────────────────────── */

/** Best-effort filename extraction from a MinIO object key like "incidents/{guid}/foo.jpg". */
export function fileNameFromKey(key: string): string {
  if (!key) return '';
  return key.split('/').pop() ?? key;
}

/** Heuristic from filename when we don't have a Content-Type header. */
export function isImageByFilename(name: string): boolean {
  return /\.(jpe?g|png|gif|webp|heic|heif|avif|bmp)$/i.test(name);
}
